import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateMenu } from "../../features/menu/menuSlice";
import { useNavigate, useParams } from "react-router-dom";
import { APP_ROUTES_LABEL } from "../../constants/appConstants";
import { AuthContext } from "../../context/AuthContext";
import { sidebarMenuSections, NavigationMenu } from "../../constants/navigationConfig";

// Legacy menuList export for backward compatibility (used by InputForm)
export const menuList = [
  {
    name: "outputs",
    children: NavigationMenu.map((item) => ({
      name: item.name,
      id: item.id,
    })),
  },
];

const Sidebar = () => {
  const { currentMenu } = useSelector((state) => state.menu);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const handleClick = (item) => {
    navigate(item.route, { replace: true });
    dispatch(
      updateMenu({
        currentMenu: item.id,
        currentMenuLabel: APP_ROUTES_LABEL[item.id],
      })
    );
  };

  useEffect(() => {
    if (id) {
      dispatch(
        updateMenu({
          currentMenu: id,
          currentMenuLabel: APP_ROUTES_LABEL[id],
        })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (user && !id) {
      dispatch(
        updateMenu({
          currentMenu: user.admin ? "users" : "home",
          currentMenuLabel: user.admin ? "Users" : "Home",
        })
      );
    }
  }, [dispatch, id, user]);

  const canViewItem = (item, sectionId) => {
    if (!user) return false;
    if (item.adminOnly && !user.admin) return false;
    if (item.alwaysVisible) return true;
    if (sectionId !== "dashboard" && user.page_view && !user.page_view.includes(item.id)) {
      return false;
    }
    return true;
  };

  return (
    <aside 
      className="flex flex-col h-full"
      style={{ backgroundColor: '#0f172a' }}
    >
      {/* Logo */}
      <div 
        className="h-14 flex items-center px-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <span className="font-semibold text-lg hidden md:block" style={{ color: '#f1f5f9' }}>Clinica AI</span>
        <span className="font-bold text-lg md:hidden mx-auto" style={{ color: '#f1f5f9' }}>C</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {sidebarMenuSections.map((section) => {
          const visibleItems = section.items.filter((item) => canViewItem(item, section.id));
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.id} className="mb-1">
              {/* Section Label */}
              {section.label && (
                <div className="px-4 py-2 hidden md:block">
                  <span 
                    className="text-[11px] font-semibold uppercase tracking-wider"
                    style={{ color: '#94a3b8' }}
                  >
                    {section.label}
                  </span>
                </div>
              )}

              {/* Menu Items */}
              <div className="px-2 space-y-0.5">
                {visibleItems.map((item) => {
                  const isActive = currentMenu === item.id;
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleClick(item)}
                      title={item.name}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 ease-in-out"
                      style={{
                        backgroundColor: isActive ? '#ffffff' : 'transparent',
                        color: isActive ? '#0f172a' : '#e2e8f0',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon 
                        className="w-5 h-5 flex-shrink-0"
                        style={{ color: isActive ? '#0f172a' : '#e2e8f0' }}
                      />
                      <span 
                        className="text-[13px] font-medium leading-tight text-left hidden md:block"
                        style={{ color: isActive ? '#0f172a' : '#e2e8f0' }}
                      >
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User Section */}
      {user && (
        <div 
          className="p-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="flex items-center gap-3 px-2">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
              style={{ backgroundColor: '#3b82f6', color: '#ffffff' }}
            >
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="hidden md:block flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: '#f1f5f9' }}>{user.name}</p>
              <p className="text-xs truncate" style={{ color: '#94a3b8' }}>{user.email}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
